import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Flame, Droplet, Pill, AlertTriangle, Ban, Sparkles, Flag, CupSoda, Calculator } from 'lucide-react';
import RaceCountdown from '../RaceCountdown';
import type { FuelPanel, FuelPlan, MorningStep, Race, Wave } from './types';
import { addMinutes, parseFinish, plannerStorageKey, type PlannerSettings } from './raceTime';

const panelIcons: Record<FuelPanel['icon'], React.ReactNode> = {
  pill: <Pill size={16} />,
  droplet: <Droplet size={16} />,
  ban: <Ban size={16} />,
  alert: <AlertTriangle size={16} />,
  flag: <Flag size={16} />,
  cup: <CupSoda size={16} />,
};

/** Generic morning for races without a written plan, timed off the gun. */
const DEFAULT_MORNING: MorningStep[] = [
  { offset: -240, what: 'Wake · 300–500 mL water + pinch of salt', note: 'Rehydrate' },
  { offset: -210, what: 'Pre-race meal: familiar, low-fibre carbs (toast, jam, banana, rice) · coffee if you use it', note: '1–4 g CHO/kg', variant: 'key' },
  { offset: -150, until: -90, what: 'Sip 400–600 mL fluid with electrolytes · travel to start' },
  { offset: -90, what: 'Stop bulk fluid · bathroom · small sips only' },
  { offset: -45, what: 'Easy jog, drills, a few strides' },
  { offset: -15, what: 'Optional gel + small sip of water', variant: 'key' },
  { offset: 0, what: 'GUN', variant: 'gun' },
];

const LOAD_STYLES = {
  three: { label: '3-day · ramp + 2 full', factors: [8, 11, 11.5] },
  two: { label: '2-day · classic 36–48 h', factors: [10, 12] },
} as const;

type LoadStyle = keyof typeof LOAD_STYLES;

type CalcSettings = PlannerSettings;

const formatOffset = (offset: number): string => {
  if (offset === 0) return 'Gun';
  const abs = Math.abs(offset);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `T-${h > 0 ? `${h}:${String(m).padStart(2, '0')}` : `${m} min`}`;
};

const loadSettings = (key: string, fallback: CalcSettings): CalcSettings => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...fallback, ...(JSON.parse(raw) as Partial<CalcSettings>) } : fallback;
  } catch {
    return fallback;
  }
};

const CarbCalculator: React.FC<{
  settings: CalcSettings;
  waves: Wave[];
  onChange: (next: CalcSettings) => void;
}> = ({ settings, waves, onChange }) => {
  const { weightKg, loadStyle, finish, ratePerHour } = settings;
  const factors = LOAD_STYLES[loadStyle].factors;
  const finishMin = parseFinish(finish);
  const inRaceCho = finishMin ? Math.round((ratePerHour * finishMin) / 60) : null;
  const gels = inRaceCho ? Math.round(inRaceCho / 30) : null;
  const gelEvery = finishMin && gels ? Math.round((finishMin - 15) / gels) : null;
  const set = (patch: Partial<CalcSettings>) => onChange({ ...settings, ...patch });

  return (
    <div className="calc-card">
      <div className="calc-inputs">
        <label className="calc-field">
          <span>Body weight</span>
          <div className="calc-input-wrap">
            <input
              type="number"
              min={35}
              max={150}
              step={0.5}
              value={weightKg}
              onChange={(e) => set({ weightKg: Number(e.target.value) || 0 })}
            />
            <em>kg</em>
          </div>
        </label>
        <label className="calc-field">
          <span>Load style</span>
          <select value={loadStyle} onChange={(e) => set({ loadStyle: e.target.value as LoadStyle })}>
            {(Object.keys(LOAD_STYLES) as LoadStyle[]).map((k) => (
              <option key={k} value={k}>
                {LOAD_STYLES[k].label}
              </option>
            ))}
          </select>
        </label>
        {waves.length > 1 && (
          <label className="calc-field">
            <span>Start wave</span>
            <select value={settings.waveId} onChange={(e) => set({ waveId: e.target.value })}>
              {waves.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.label} · {w.time}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="calc-field">
          <span>Goal finish</span>
          <div className="calc-input-wrap">
            <input
              type="text"
              inputMode="numeric"
              placeholder="3:30"
              value={finish}
              onChange={(e) => set({ finish: e.target.value })}
            />
            <em>h:mm</em>
          </div>
        </label>
        <label className="calc-field">
          <span>In-race carbs</span>
          <select value={ratePerHour} onChange={(e) => set({ ratePerHour: Number(e.target.value) })}>
            {[60, 75, 90, 100].map((r) => (
              <option key={r} value={r}>
                {r} g/h{r === 90 ? ' · trained gut' : r === 60 ? ' · untrained gut' : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="calc-results">
        {factors.map((f, i) => (
          <div key={i} className="calc-result">
            <span className="calc-result-lbl">T-{factors.length - i}</span>
            <span className="calc-result-val">
              <em>{weightKg > 0 ? Math.round(weightKg * f) : '—'}</em> g
            </span>
            <span className="calc-result-sub">{f} g/kg</span>
          </div>
        ))}
        <div className="calc-result">
          <span className="calc-result-lbl">Pre-race meal</span>
          <span className="calc-result-val">
            <em>{weightKg > 0 ? Math.round(weightKg * 2.5) : '—'}</em> g
          </span>
          <span className="calc-result-sub">2.5 g/kg · 3–4 h before</span>
        </div>
        <div className="calc-result">
          <span className="calc-result-lbl">In race</span>
          <span className="calc-result-val">
            <em>{inRaceCho ?? '—'}</em> g
          </span>
          <span className="calc-result-sub">
            {gels && gelEvery ? `≈ ${gels} × 30 g gels · every ~${gelEvery} min` : 'enter a goal like 3:30'}
          </span>
        </div>
      </div>
      <p className="calc-footnote">
        Targets follow current sports-nutrition guidance (10–12 g/kg/day for 36–48 h, 1–4 g/kg pre-race, 60–90 g/h
        in-race). Practise in-race rates in training — don{"'"}t debut 90 g/h on race day.
      </p>
    </div>
  );
};

const FuellingView: React.FC<{ race: Race; plan?: FuelPlan; label: string; gunLabel: string }> = ({
  race,
  plan,
  label,
  gunLabel,
}) => {
  const storageKey = plannerStorageKey(race.id);
  const waves = useMemo(
    () => plan?.waves ?? [{ id: 'main', label: 'Start', time: race.startTime }],
    [plan, race.startTime],
  );
  const [settings, setSettings] = useState<CalcSettings>(() =>
    loadSettings(storageKey, {
      weightKg: plan?.referenceWeightKg ?? 70,
      loadStyle: 'three',
      waveId: plan?.defaultWaveId ?? waves[0].id,
      finish: '',
      ratePerHour: 90,
    }),
  );

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(settings));
    } catch {
      // Storage unavailable (private mode) — settings just won't persist.
    }
  }, [storageKey, settings]);

  const wave = waves.find((w) => w.id === settings.waveId) ?? waves[0];
  const morning = plan?.morning ?? DEFAULT_MORNING;

  return (
    <>
      <motion.section
        className="fuel-hero ev-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="fuel-hero-inner">
          <motion.div
            className="fuel-hero-eyebrow"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <span className="fuel-hero-dot" />
            <span>Race-Week Protocol</span>
            <span className="fuel-hero-sep">·</span>
            <span>{label}</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.8 }}
          >
            Carb Load <span className="fuel-amp">&amp;</span> Race Morning
          </motion.h1>
          <RaceCountdown raceDate={race.date} timezone={race.timezone} detail={gunLabel} />
        </div>
      </motion.section>

      <section className="ev-section">
        <h2 className="section-title ev-section-title">Carb-load planner</h2>
        <p className="ev-sub">
          Your numbers for {race.name}. Settings save on this device; the race-morning timeline below follows your
          wave.
        </p>
        <CarbCalculator settings={settings} waves={waves} onChange={setSettings} />
      </section>

      {plan && (
        <section className="ev-section">
          <h2 className="section-title ev-section-title">Load outline</h2>
          {settings.weightKg !== plan.referenceWeightKg && (
            <p className="ev-sub">
              <Calculator size={13} style={{ verticalAlign: '-2px' }} /> The written plan below is for a{' '}
              {plan.referenceWeightKg} kg runner — use the planner targets above for your own grams.
            </p>
          )}
          <div className="fuel-ticker">
            {plan.ticker.map((t) => (
              <div key={t.label} className="fuel-tick">
                <span className="fuel-tick-lbl">{t.label}</span>
                <span className="fuel-tick-val">
                  <em>{t.value}</em> {t.unit}
                </span>
                <span className="fuel-tick-sub">{t.sub}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {plan?.calendarRows && plan.calendarDates && (
        <section className="ev-section">
          <h2 className="section-title ev-section-title">Supplement loading schedule</h2>
          {plan.calendarSub && <p className="ev-sub">{plan.calendarSub}</p>}
          <div className="fuel-cal-wrap">
            <div
              className="fuel-cal"
              style={{ gridTemplateColumns: `minmax(220px, 1.4fr) repeat(${plan.calendarDates.length}, minmax(70px, 1fr))` }}
            >
              <div className="fuel-cal-head fuel-cal-head-first">Supplement · dose</div>
              {plan.calendarDates.map((d) => (
                <div key={d} className="fuel-cal-head">
                  {d}
                </div>
              ))}
              {plan.calendarRows.map((row) => (
                <React.Fragment key={row.name}>
                  <div className="fuel-cal-row-name">
                    <strong>{row.name}</strong>
                    <span>{row.sub}</span>
                  </div>
                  {row.cells.map((cell, i) => (
                    <div key={i} className={`fuel-cal-cell fuel-cal-${cell.tone}`}>
                      {cell.label}
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>
      )}

      {plan && (
        <section className="ev-section">
          <h2 className="section-title ev-section-title">Daily carb load</h2>
          <p className="ev-sub">{plan.daysSub}</p>
          <div className="fuel-days">
            {plan.days.map((d) => (
              <div key={d.day} className="fuel-day-card">
                <div className="fuel-day-head">
                  <div className="fuel-day-heading">
                    <Flame size={18} />
                    <div>
                      <h3>{d.day}</h3>
                      <span className="fuel-day-tag">{d.tag}</span>
                    </div>
                  </div>
                  <span className="fuel-day-kcal">{d.kcal}</span>
                </div>
                <ul className="fuel-meals">
                  {d.meals.map((m, i) => (
                    <li key={i}>
                      <span className="fuel-meal-time">{m.time}</span>
                      <span className="fuel-meal-desc">{m.description}</span>
                      <span className="fuel-meal-cho">
                        {m.cho}
                        <span className="fuel-meal-unit">g C</span>
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="fuel-day-totals">
                  <div>
                    <span>CHO</span>
                    <strong>{d.totals.cho}</strong>
                  </div>
                  <div>
                    <span>Protein</span>
                    <strong>{d.totals.protein}</strong>
                  </div>
                  <div>
                    <span>Fat</span>
                    <strong>{d.totals.fat}</strong>
                  </div>
                  <div>
                    <span>{d.totals.sodium ? 'Fluid · Na' : 'Fluid'}</span>
                    <strong>{d.totals.sodium ? `${d.totals.fluid} · ${d.totals.sodium}` : d.totals.fluid}</strong>
                  </div>
                </div>
                {d.note && <p className="fuel-day-note">{d.note}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="ev-section">
        <h2 className="section-title ev-section-title">{plan?.morningTitle ?? 'Race morning'}</h2>
        <p className="ev-sub">
          {plan?.morningSub ?? 'Lay kit out the night before. Times follow your start wave.'} Timed for{' '}
          <strong>
            {wave.label} · {wave.time}
          </strong>
          {wave.detail ? ` (${wave.detail})` : ''}.
        </p>
        <div className="fuel-timeline">
          {morning.map((step, i) => (
            <div key={i} className={`fuel-step fuel-step-${step.variant ?? 'default'}`}>
              <div className="fuel-step-time">
                {addMinutes(wave.time, step.offset)}
                {step.until !== undefined ? `–${addMinutes(wave.time, step.until)}` : ''}
              </div>
              <div className="fuel-step-what">{step.what}</div>
              <div className="fuel-step-note">
                {step.variant === 'gun' ? step.note : [formatOffset(step.offset), step.note].filter(Boolean).join(' · ')}
              </div>
            </div>
          ))}
        </div>

        {plan && plan.raceRules.length > 0 && (
          <div className="fuel-race-rules">
            {plan.raceRules.map((r) => (
              <div key={r.label}>
                <span className="fuel-race-rule-lbl">{r.label}</span>
                <p>{r.body}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {plan && plan.panels.length > 0 && (
        <section className="ev-section">
          <div className="fuel-panels">
            {plan.panels.map((p) => (
              <div key={p.title} className={`fuel-panel ${p.wide ? 'fuel-panel-wide' : ''}`}>
                <div className="fuel-panel-head">
                  {panelIcons[p.icon]}
                  <h3>{p.title}</h3>
                  <span className="fuel-panel-tag">{p.tag}</span>
                </div>
                {p.kind === 'list' && p.rows && (
                  <ul className="fuel-panel-list">
                    {p.rows.map((r) => (
                      <li key={r.key}>
                        <span className="fuel-panel-key">{r.key}</span>
                        <span>{r.text}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {p.kind === 'text' && <p className="fuel-panel-body">{p.text}</p>}
                {p.kind === 'grid' && p.grid && (
                  <div className="fuel-panel-grid">
                    {p.grid.map((g) => (
                      <div key={g.title}>
                        <strong className="fuel-panel-hl">{g.title}</strong>
                        <p>{g.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <motion.footer
        className="footer ev-footer fuel-footer"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="footer-content">
          <Sparkles size={28} />
          <h3>{plan?.footerTitle ?? 'Race-week fuelling'}</h3>
          <p>{plan?.footerSub ?? 'Expect to weigh 1.5–2 kg more after a full load — that’s stored glycogen and water.'}</p>
        </div>
      </motion.footer>
    </>
  );
};

export default FuellingView;
